import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Users, Trash2, Crown, Edit, Eye, Mail, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";

type MemberRole = "owner" | "editor" | "viewer";

interface Member {
  id: string;
  user_id: string;
  role: MemberRole;
  invited_at: string;
  accepted_at: string | null;
  email?: string;
  full_name?: string;
}

interface ManageMembersDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  type: "workspace" | "course";
  resourceId: string;
  resourceName: string;
  isOwner: boolean;
}

const roleIcons: Record<MemberRole, React.ReactNode> = {
  owner: <Crown className="h-4 w-4 text-yellow-500" />,
  editor: <Edit className="h-4 w-4 text-blue-500" />,
  viewer: <Eye className="h-4 w-4 text-gray-500" />,
};

const roleLabels: Record<MemberRole, string> = {
  owner: "Owner",
  editor: "Editor",
  viewer: "Viewer",
};

const roleDescriptions: Record<MemberRole, string> = {
  owner: "Full access, can manage members",
  editor: "Can view and edit content",
  viewer: "Read-only access",
};

export default function ManageMembersDialog({
  open,
  onOpenChange,
  type,
  resourceId,
  resourceName,
  isOwner,
}: ManageMembersDialogProps) {
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<MemberRole>("viewer");
  const [inviting, setInviting] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  const tableName = type === "workspace" ? "workspace_members" : "course_members";
  const foreignKey = type === "workspace" ? "workspace_id" : "course_id";

  useEffect(() => {
    if (open) {
      fetchMembers();
      getCurrentUser();
    }
  }, [open, resourceId]);

  const getCurrentUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    setCurrentUserId(user?.id || null);
  };

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from(tableName)
        .select("*")
        .eq(foreignKey, resourceId);

      if (error) throw error;

      // Fetch user details for each member
      const membersWithDetails = await Promise.all(
        (data || []).map(async (member: Member) => {
          const { data: profile } = await supabase
            .from("profiles")
            .select("email, full_name")
            .eq("id", member.user_id)
            .single();

          return {
            ...member,
            email: profile?.email || "Unknown",
            full_name: profile?.full_name || null,
          };
        })
      );

      setMembers(membersWithDetails);
    } catch (error) {
      console.error("Error fetching members:", error);
      toast.error("Failed to load members");
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim()) {
      toast.error("Please enter an email address");
      return;
    }

    setInviting(true);
    try {
      // Find user by email
      const { data: profile, error: profileError } = await supabase
        .from("profiles")
        .select("id")
        .eq("email", inviteEmail.trim().toLowerCase())
        .single();

      if (profileError || !profile) {
        toast.error("User not found. They must have an account first.");
        return;
      }

      // Check if already a member
      const existingMember = members.find((m) => m.user_id === profile.id);
      if (existingMember) {
        toast.error("This user is already a member");
        return;
      }

      // Add member
      const { data: { user } } = await supabase.auth.getUser();
      const { error: insertError } = await supabase.from(tableName).insert({
        [foreignKey]: resourceId,
        user_id: profile.id,
        role: inviteRole,
        invited_by: user?.id,
        accepted_at: new Date().toISOString(), // Auto-accept for now
      });

      if (insertError) throw insertError;

      toast.success(`Added ${inviteEmail} as ${roleLabels[inviteRole]}`);
      setInviteEmail("");
      setInviteRole("viewer");
      fetchMembers();
    } catch (error) {
      console.error("Error inviting member:", error);
      toast.error("Failed to add member");
    } finally {
      setInviting(false);
    }
  };

  const handleUpdateRole = async (memberId: string, newRole: MemberRole) => {
    try {
      const { error } = await supabase
        .from(tableName)
        .update({ role: newRole })
        .eq("id", memberId);

      if (error) throw error;

      toast.success("Role updated");
      fetchMembers();
    } catch (error) {
      console.error("Error updating role:", error);
      toast.error("Failed to update role");
    }
  };

  const handleRemoveMember = async (memberId: string, memberEmail: string) => {
    if (!confirm(`Remove ${memberEmail} from this ${type}?`)) return;

    try {
      const { error } = await supabase
        .from(tableName)
        .delete()
        .eq("id", memberId);

      if (error) throw error;

      toast.success("Member removed");
      fetchMembers();
    } catch (error) {
      console.error("Error removing member:", error);
      toast.error("Failed to remove member");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Manage {type === "workspace" ? "Workspace" : "Course"} Members
          </DialogTitle>
          <DialogDescription>
            Share "{resourceName}" with other users
          </DialogDescription>
        </DialogHeader>

        {/* Invite Section */}
        {isOwner && (
          <div className="space-y-3 border-b pb-4">
            <Label>Add a member</Label>
            <div className="flex gap-2">
              <Input
                type="email"
                placeholder="Enter email address"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                className="flex-1"
              />
              <Select
                value={inviteRole}
                onValueChange={(v) => setInviteRole(v as MemberRole)}
              >
                <SelectTrigger className="w-28">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="viewer">
                    <div className="flex items-center gap-2">
                      {roleIcons.viewer}
                      Viewer
                    </div>
                  </SelectItem>
                  <SelectItem value="editor">
                    <div className="flex items-center gap-2">
                      {roleIcons.editor}
                      Editor
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={handleInvite} disabled={inviting}>
                {inviting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Mail className="h-4 w-4" />
                )}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              <strong>Editor:</strong> Can edit content • <strong>Viewer:</strong> Read-only access
            </p>
          </div>
        )}

        {/* Members List */}
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : members.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              No members yet. Add someone to collaborate!
            </p>
          ) : (
            members.map((member) => (
              <div
                key={member.id}
                className="flex items-center justify-between p-3 rounded-lg border bg-card"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-medium">
                    {(member.full_name || member.email || "?")[0].toUpperCase()}
                  </div>
                  <div>
                    <p className="font-medium text-sm">
                      {member.full_name || member.email}
                      {member.user_id === currentUserId && (
                        <Badge variant="outline" className="ml-2 text-xs">
                          You
                        </Badge>
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {member.email}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {isOwner && member.user_id !== currentUserId ? (
                    <>
                      <Select
                        value={member.role}
                        onValueChange={(v) =>
                          handleUpdateRole(member.id, v as MemberRole)
                        }
                      >
                        <SelectTrigger className="w-28">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="viewer">
                            <div className="flex items-center gap-2">
                              {roleIcons.viewer}
                              Viewer
                            </div>
                          </SelectItem>
                          <SelectItem value="editor">
                            <div className="flex items-center gap-2">
                              {roleIcons.editor}
                              Editor
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-destructive hover:text-destructive"
                        onClick={() =>
                          handleRemoveMember(member.id, member.email || "")
                        }
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </>
                  ) : (
                    <Badge variant="secondary" className="flex items-center gap-1">
                      {roleIcons[member.role]}
                      {roleLabels[member.role]}
                    </Badge>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

